import { useBreakpointValue } from "@chakra-ui/react"
import Head from "next/head"
import { useRouter } from "next/router"
import { useRecoilValue } from "recoil"
import {
  brands,
  categoryLevels,
  FH_URL_PARAMS,
  pages,
  searchNavigationParams,
} from "../../../../common/constants/constants"
import { breadcrumbs, tabs } from "../../../../common/constants/search-constants"
import { useBrandedTranslation } from "../../../../common/hooks/useBrandedTranslation"
import { useQueryParam } from "../../../../common/lib/QueryParamContext"
import { useUrlParams } from "../../../../common/lib/UrlParamsContext"
import { searchServiceLocaleState } from "../../../../common/store/settingsStore"
import { BreadcrumbItemType, Tab } from "../../../../common/types/global"
import { FhResponseData } from "../../../../common/types/search"
import { getBaseURLwithLocale } from "../../../../common/utils/common-utils"
import { getNewPathWithParam } from "../../../../common/utils/router-utils"
import { prepareStructuredMicrodataForBreadcrumbs } from "../../../../common/utils/schema-org-utils"
import {
  getTrimmedUrlParams,
  isBreadcrumbBannedCategory,
  isRootCategoryBreadCrumb,
} from "../../../../common/utils/search-utils"
import Breadcrumbs from "../../../shared/Breadcrumbs/Breadcrumbs"

type Props = {
  breadcrumbsData: FhResponseData
  selectedTab: Tab
  host: string
}

const SearchBreadcrumbs = ({ breadcrumbsData, selectedTab, host }: Props) => {
  const { asPath, locale } = useRouter()
  const searchServiceLocale = useRecoilValue(searchServiceLocaleState)
  const { setUrlParams } = useUrlParams()
  const { queryParam } = useQueryParam()
  const { t } = useBrandedTranslation()
  const isMobileCrumbs = useBreakpointValue({ base: true, sm: false })
  const bannedTypes = new Set(["home", "selected-universe", "search"])
  const allowedAttributeTypes = new Set([breadcrumbs.CATEGORIES])

  const crumbs: BreadcrumbItemType[] = []

  const breadcrumbClick = (url_params: string) => {
    setUrlParams(url_params)
  }

  /* This is used on all variations of the Search page */
  const getSearchHref = (url_params: string) => {
    const trimmedUrlParams = getTrimmedUrlParams(url_params)
    return getNewPathWithParam(asPath, FH_URL_PARAMS, trimmedUrlParams)
  }

  /* This is used on lister pages
   Series Lister Pages don't have Search Breadcrumbs component, this is used only on System Lister Pages */
  const getListerPageHref = (non_ml: string) => {
    /* non_ml value comes in format {catalog01_roc374547xch_ch1100000xch_ch2100001xch_ch3132761xch}
      Extract the id after the last underscore and re-format it to Categories API id format to get CH1_132761. */

    /* This part gives us "ch3132761" */
    const searchCategoryId = non_ml.split("_")?.at(-1).split("x")?.[0]

    /* Format it to "CH3_132761" */
    const categoryId = (searchCategoryId.slice(0, 3) + "_" + searchCategoryId.slice(3)).toUpperCase()

    /* For chapters level 2 and 3 check on which tab we are currently on and apply the proper product type to the href */
    const productType = categoryId.startsWith(categoryLevels.ONE)
      ? ""
      : `/${selectedTab === tabs.PRO ? pages.PRODUCTS : pages.SPARE_PARTS}`

    return `/${pages.SYSTEMS}/${categoryId}${productType}`
  }

  const isListerPage =
    queryParam.name === searchNavigationParams.SYSTEMS || queryParam.name === searchNavigationParams.SERIES

  /* When we are referred to the search page from Systems or Series navigation show custom static breadcrumb pointing to the old interface */
  if (isListerPage) {
    crumbs.push({ name: queryParam.mainCategoryName, href: queryParam.mainCategoryUrl })
  }

  /* Loop through the data in the response to construct the correct breadcrumbs for the particular search page variant */
  breadcrumbsData.forEach((crumbData) => {
    const { name, url_params } = crumbData
    // BE data is not consistent. In some cases we have name.type in other name.attribute_type
    const isBannedType = name?.type && bannedTypes.has(name.type)
    const isAllowedAttributeType = name?.attribute_type && allowedAttributeTypes.has(name.attribute_type)
    const isBannedCategory = isBreadcrumbBannedCategory(name.non_ml)
    const isNovelties =
      crumbData.name.attribute_type === `${breadcrumbs.NEW}_${searchServiceLocale.toLowerCase()}` &&
      queryParam?.name === searchNavigationParams.NEW
    const isCross =
      crumbData.name.attribute_type === `${breadcrumbs.CROSS}_${searchServiceLocale.toLowerCase()}` &&
      queryParam?.name === searchNavigationParams.CROSS
    const isAllSpareParts =
      queryParam?.name === searchNavigationParams.ALL_SPARE_PARTS && isRootCategoryBreadCrumb(name.non_ml)
    const isMixAndMatch =
      queryParam?.name === searchNavigationParams.MIX_AND_MATCH && isRootCategoryBreadCrumb(name.non_ml)

    let crumbName = name?.value

    /* Search for: Search word breadcrumb is only shown on the results page, not on lister pages */
    if (
      name.type === breadcrumbs.SEARCH ||
      name.attribute_type === breadcrumbs.CHARACTERISTICS ||
      name.attribute_type === breadcrumbs.APPLICATION_PURPOSES ||
      isNovelties ||
      isCross ||
      isAllSpareParts ||
      isMixAndMatch
    ) {
      if (isNovelties) {
        crumbName = t("nav-newReleases")
      } else if (isCross) {
        crumbName = t("search-cross-selling-page-title", { brandName: brands[queryParam.value]?.name })
      } else if (isAllSpareParts) {
        crumbName = t("nav-spareParts")
      } else if (isMixAndMatch) {
        crumbName = t("mix-and-match-page-title")
      } else {
        crumbName = crumbData.searchterms?.term?.value || name?.value
      }

      crumbs.push({
        name: isMobileCrumbs ? t("search-all-search-results") : `${t("search-prefix")}: ${crumbName}`,
        href: getSearchHref(url_params),
        clickHandler: () => breadcrumbClick(url_params),
      })
    }

    if (!isBannedType && isAllowedAttributeType && !isBannedCategory) {
      crumbs.push({
        name: crumbName,
        href: isListerPage ? getListerPageHref(name?.non_ml) : getSearchHref(url_params),
        clickHandler: isListerPage ? null : () => breadcrumbClick(url_params),
      })
    }
  })

  if (queryParam.name === searchNavigationParams.SYSTEMS) {
    const baseURL = getBaseURLwithLocale(host, locale)
    const breadcrumbsSchemaMarkup = prepareStructuredMicrodataForBreadcrumbs(crumbs, baseURL?.href)

    return (
      <>
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbsSchemaMarkup) }}
          />
        </Head>
        <Breadcrumbs crumbs={crumbs} />
      </>
    )
  }

  return <Breadcrumbs crumbs={crumbs} />
}

export default SearchBreadcrumbs
